import type { ApiClient } from '../apiClient';
import type { SessionTokens } from './session';

export interface Invite {
  inviteId: string;
  clientEmail: string;
  clientDisplayName: string | null;
  expiresAt: string;
}

export interface SignupInput {
  token: string;
  fullName: string;
  /** ISO YYYY-MM-DD. */
  dateOfBirth: string;
  email: string;
  /** E.164, e.g. +15551234567. */
  phone: string;
}

export interface SignupResult {
  userId: string;
  emailOtpId: string;
  smsOtpId: string;
}

export function resolveInvite(api: ApiClient, token: string): Promise<Invite> {
  return api.get<Invite>(`/api/auth/welcome?t=${encodeURIComponent(token)}`);
}

export function signup(api: ApiClient, input: SignupInput): Promise<SignupResult> {
  return api.post<SignupResult>('/api/auth/signup', input);
}

export interface VerifyEmailOtpResult {
  verified: boolean;
  /**
   * Short-lived JWT returned ONLY when `verified` is true — proves this
   * caller (not merely someone who knows the email) just verified the
   * signup OTP. Required by `enrollPasskey` for FIRST-ENROLL (S6c 1b
   * CRITICAL fix). Absent if verification failed.
   */
  enrollmentTicket?: string;
}

export function verifyEmailOtp(api: ApiClient, email: string, code: string): Promise<VerifyEmailOtpResult> {
  return api.post<VerifyEmailOtpResult>('/api/auth/otp/email/verify', { channel: 'email', target: email, code });
}

export function resendEmailOtp(api: ApiClient, email: string): Promise<{ otpId: string; expiresInSeconds: number }> {
  return api.post('/api/auth/otp/email/send', { channel: 'email', target: email });
}

export function verifySmsOtp(api: ApiClient, phone: string, code: string): Promise<{ verified: boolean }> {
  return api.post<{ verified: boolean }>('/api/auth/otp/sms/verify', { channel: 'sms', target: phone, code });
}

export function refreshSession(api: ApiClient, refreshToken: string): Promise<SessionTokens> {
  return api.post<SessionTokens>('/api/auth/login/refresh', { refreshToken });
}

// ---------------------------------------------------------------------------
// Password sign-in
// ---------------------------------------------------------------------------

/**
 * A password gets you your ACCOUNT. It does not, by itself, open your sealed
 * messages — those are unlocked by the Recovery Kit on a device that has never
 * held your key. That separation is the reason a stolen password is not a
 * stolen archive, and it is why the sign-in screen says so out loud.
 */
export function passwordLogin(
  api: ApiClient, email: string, password: string,
): Promise<SessionTokens> {
  return api.post('/api/auth/password/login', { email, password });
}

/** Always resolves. The server refuses to say whether the address has an
 *  account, and the UI must not imply otherwise. */
export function requestPasswordLink(api: ApiClient, email: string): Promise<{ sent: true }> {
  return api.post('/api/auth/password/request', { email, app: 'member' });
}

export function setPassword(
  api: ApiClient, ticket: string, password: string,
): Promise<SessionTokens> {
  return api.post('/api/auth/password/set', { ticket, password });
}
